import { liquidGlassSurfaceFillSx, translateTemplateText, useLiquidGlassPress } from '@focuslee/shared-mini-app';
import { Box, Stack } from '@mui/material';
import { Text } from '@telegram-apps/telegram-ui';
import { hapticFeedback } from '@tma.js/sdk';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { ListCategory, ListGoal, ListQuest } from '../../client/types';
import { getColor } from '../../helpers/color';
import { msToShortTime } from '../../helpers/datetime';
import { CategoryIcon } from '../common/CategoryIcon';
import { CommonIcon } from '../common/CommonIcon';
import { DueTime } from '../common/DueTime';
import { ExternalQuestButton } from './ExternalQuestButton';
import { FocusModeButton } from './focus/FocusModeButton';
import { QuestCompleteButton } from './QuestCompleteButton';

const TRAILING_ZONE_WIDTH = 56;

export type QuestItemCellProps = {
    quest: ListQuest;
    overdue: boolean;
    category: ListCategory;
    goal: ListGoal | null;
    goalCategory: ListCategory | null;
    questXP: number;
    onComplete: (completion: any) => void;
    onClick: () => void;
    onPressedChange?: (pressed: boolean) => void;
    dueDateVerbose?: boolean;
};

export const QuestItemCell = ({
    quest,
    overdue,
    category,
    goal,
    goalCategory,
    questXP,
    onComplete,
    onClick,
    onPressedChange,
    dueDateVerbose = false
}: QuestItemCellProps) => {
    const { t } = useTranslation();
    const hintColor = "var(--tgui--hint_color)";
    const questName = translateTemplateText(t, quest.templateId, 'name', quest.name);

    const { onPointerDown, onPointerMove, onPointerUp, onPointerCancel, onClick: onMainClick } = useLiquidGlassPress({
        preventDefault: true,
        onPressedChange,
        onActivate: () => {
            hapticFeedback.impactOccurred.ifAvailable('light');
            onClick();
        },
    });

    return (
        <Box
            sx={{ width: '100%' }}
        >
            <Box sx={{ display: 'flex', alignItems: 'stretch', width: '100%', boxSizing: 'border-box', borderRadius: '12px', overflow: 'hidden', ...liquidGlassSurfaceFillSx }}>
                <Box
                    component="button"
                    type="button"
                    onClick={onMainClick}
                    onPointerDown={onPointerDown}
                    onPointerMove={onPointerMove}
                    onPointerUp={onPointerUp}
                    onPointerCancel={onPointerCancel}
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        m: 0,
                        p: '11px 14px',
                        border: 'none',
                        background: 'none',
                        textAlign: 'left',
                        cursor: 'pointer',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '3px',
                        font: 'inherit',
                        color: 'inherit',
                        WebkitTapHighlightColor: 'transparent',
                        outline: 'none',
                    }}
                >
                    <Text
                        style={{
                            fontSize: 15,
                            fontWeight: 500,
                            lineHeight: 1.3,
                            color: 'var(--tg-theme-text-color)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            display: 'block',
                            width: '100%',
                        }}
                    >
                        {questName}
                    </Text>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Stack direction="row" alignItems="center" gap="5px">
                            {quest.repeat && <CommonIcon type="filled" color={hintColor} icon="repeat" size={13} />}
                            <DueTime date={dayjs(quest.dueDate)} dueDateVerbose={overdue || dueDateVerbose} />
                            {(quest.timing || !!quest.timedInterval) &&
                                <Stack direction="row" alignItems="center" gap="3px">
                                    <CommonIcon type={quest.timing ? "outlined" : "filled"} color={hintColor} icon={"timer"} size={13} />
                                    <Text style={{ fontSize: '13px', color: hintColor }}>
                                        {quest.timing && quest.timedInterval
                                            ? `${msToShortTime(quest.timerValueInMs!)} / ${msToShortTime(quest.timedInterval * 60 * 1000)}`
                                            : quest.timing
                                                ? msToShortTime(quest.timerValueInMs!)
                                                : msToShortTime((quest.timedInterval || 0) * 60 * 1000)
                                        }
                                    </Text>
                                </Stack>
                            }
                        </Stack>
                        <Stack direction="row" justifyContent="center" alignItems="center">
                            <Box sx={{
                                width: 20,
                                height: 20,
                                borderRadius: 1,
                                background: getColor(category.iconColor),
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <CategoryIcon
                                    icon={category.icon}
                                    iconColor={category.iconColor}
                                    customIcon={category.customIcon}
                                    type="filled"
                                    size={14}
                                    color="var(--tg-theme-text-color)"
                                />
                            </Box>
                            <Text style={{ marginLeft: '3px', fontSize: '14px', whiteSpace: 'nowrap', color: goal && goal.active && goalCategory ? getColor(goalCategory.iconColor!) : hintColor }}>+{questXP} XP</Text>
                        </Stack>
                    </Stack>
                </Box>

                <Box
                    sx={{
                        width: '1px',
                        alignSelf: 'stretch',
                        backgroundColor: 'color-mix(in srgb, var(--tg-theme-text-color) 16%, transparent)',
                    }}
                />

                <Box sx={{ width: TRAILING_ZONE_WIDTH, flexShrink: 0 }}>
                    {quest.external
                        ? <ExternalQuestButton external={quest.external} externalDeepLink={quest.externalDeepLink} questName={questName} onPressedChange={onPressedChange} />
                        : quest.timing
                        ? <FocusModeButton questId={quest.id} onPressedChange={onPressedChange} />
                        : <QuestCompleteButton quest={quest} onComplete={onComplete} onPressedChange={onPressedChange} />}
                </Box>
            </Box>
        </Box>
    );
};
